import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import {
  Brush,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Customized
} from 'recharts';
import Form from 'react-bootstrap/Form';
import { useLocation } from 'react-router-dom';
import ChartContext from './DataEntrySection/DataEntryProvider';
import { wFlagIChartColumns, oocFlagIChartColumns } from '../components/Columns';
import {
  createCustomizedDot,
  getSigDigitsUtil,
  getEverFlagsString,
  everFlagsHasWarning,
  everFlagsHasOOC,
  liveEverFlagsConfig,
  resolveEverFlagsTooltipRows,
  formatChartPointDateTime,
  withPopupDateTime
} from '../util/util';
import { getConfiguration } from '../util/config';
import { parseArvValue, hasCenterLine, addArvLine } from '../util/arvCenterLine';

function SQCLineChart({
  data,
  lines = [],
  syncId,
  xAxis,
  yMin,
  yMax,
  isEverFlags,
  chartSigDigits,
  controlStrategyConfigurations,
  selectedSummaryInfo,
  unit = null,
  arv,
  showArv,
  onShowArvChange,
  showDateTimeInTooltip = false,
  tz,
  dateTimeFormat
}) {
  const location = useLocation();
  const isSpcDashboard = location.pathname === '/spc-dashboard';
  const formatXAxis = (tickItem) => {
    return data[tickItem]?.date ?? '';
  };
  const { currentControlStrategyConfigurations } =
    controlStrategyConfigurations || useContext(ChartContext);
  const [setupDataChecked, setSetupDataChecked] = useState(false);
  const [warningFlagsChecked, setWarningFlagsChecked] = useState(true);
  const [outOfControlChecked, setOutOfControlChecked] = useState(true);
  const [runChartsChecked, setRunChartsChecked] = useState(true);
  // Default the EverFlags overlay from the master-data toggle (isEverFlags) so
  // points colour by everFlags as soon as the chart opens when the client has it
  // enabled — the user no longer has to tick the checkbox each time (Arteco/Thijs).
  const [everFlagsChecked, setEverFlagsChecked] = useState(!!isEverFlags);
  // isEverFlags comes from an async company-type query that may resolve AFTER this
  // component first mounts; the useState initializer only runs once, so sync it in
  // when it turns on. Only reacts to isEverFlags becoming true, so a user manually
  // unchecking the overlay afterwards is preserved.
  useEffect(() => {
    if (isEverFlags) {
      setEverFlagsChecked(true);
    }
  }, [isEverFlags]);
  const config = getConfiguration(window.location.hostname);

  // "Show ARV" (Colonial): opt-in via the `arv` prop (the Control Strategy config
  // `mean`). Only offered when that value is numeric and there is a Center Line;
  // when on, a dashed dark-blue ARV line is ADDED and the Center Line is kept.
  // Uncontrolled by default; pass `showArv`/`onShowArvChange` to lift the state
  // (e.g. so the Overview print view mirrors the on-screen choice).
  const [localShowArv, setLocalShowArv] = useState(false);
  const arvValue = parseArvValue(arv);
  const canShowArv = arvValue !== null && hasCenterLine(lines);
  const arvChecked = canShowArv && (showArv !== undefined ? !!showArv : localShowArv);
  // The ARV line is drawn as a reference line (like the Center Line), so it only
  // shows while "Reference Lines" is on; the checkbox is disabled (choice kept)
  // otherwise.
  const arvApplied = arvChecked && runChartsChecked;
  const { lines: chartLines, data: chartData } = arvApplied
    ? addArvLine(lines, data, arvValue)
    : { lines, data };
  const yDomain = arvApplied ? [Math.min(yMin, arvValue), Math.max(yMax, arvValue)] : [yMin, yMax];

  const handleShowArvCheck = (e) => {
    const checked = e.target.checked;
    if (showArv === undefined) {
      setLocalShowArv(checked);
    }
    if (onShowArvChange) {
      onShowArvChange(checked);
    }
  };

  const renderSigDigits = (stringValue) => {
    return getSigDigitsUtil(
      stringValue,
      currentControlStrategyConfigurations?.chartSigDigits || chartSigDigits || 3
    );
  };

  const formatYAxis = (tickItem) => {
    return renderSigDigits(tickItem);
  };

  const handleSetupDataCheck = (e) => {
    setSetupDataChecked(e.target.checked);
  };

  const handleWarningFlagCheck = (e) => {
    setWarningFlagsChecked(e.target.checked);
  };

  const handleOutOfControlCheck = (e) => {
    setOutOfControlChecked(e.target.checked);
  };

  const handleRunChartsCheck = (e) => {
    setRunChartsChecked(e.target.checked);
  };

  const handleEverFlagsCheck = (e) => {
    setEverFlagsChecked(e.target.checked);
  };

  const renderToleranceLines = (line) => {
    if (line.name === 'User Defined LTL') {
      return (
        <Line
          type="linear"
          key={`ref-${line.yAxis}`}
          isAnimationActive={false}
          dataKey={line.yAxis}
          stroke={line.color}
          label="Test"
          name={line.name}
          dot={<CustomizedDot key={line.name} />}
          strokeDasharray="12"
        />
      );
    }
    if (line.name === 'User Defined UTL') {
      return (
        <Line
          type="linear"
          key={`ref-${line.yAxis}`}
          isAnimationActive={false}
          dataKey={line.yAxis}
          stroke={line.color}
          label="Test"
          name={line.name}
          dot={<CustomizedDot key={line.name} />}
          strokeDasharray="12"
        />
      );
    }
    return <></>;
  };

  const renderControlLines = (line) => {
    if (line.name === 'User Defined LCL') {
      return (
        <Line
          type="linear"
          key={`ref-${line.yAxis}`}
          isAnimationActive={false}
          dataKey={line.yAxis}
          stroke={line.color}
          label="Test"
          name={line.name}
          dot={<CustomizedDot key={line.name} />}
          strokeDasharray="12"
        />
      );
    }
    if (line.name === 'User Defined UCL') {
      return (
        <Line
          type="linear"
          key={`ref-${line.yAxis}`}
          isAnimationActive={false}
          dataKey={line.yAxis}
          stroke={line.color}
          label="Test"
          name={line.name}
          dot={<CustomizedDot key={line.name} />}
          strokeDasharray="12"
        />
      );
    }
    return <></>;
  };

  const renderReferenceLines = (rcc) => {
    if (!rcc) return null;
    if (rcc) {
      return (
        <>
          {chartLines
            .filter(
              (l) =>
                l.reference &&
                ![
                  'User Defined LTL',
                  'User Defined UTL',
                  'User Defined LCL',
                  'User Defined UCL'
                ].includes(l.name)
            )
            .map((line) => (
              <Line
                type="linear"
                key={`ref-${line.yAxis}`}
                isAnimationActive={false}
                dataKey={line.yAxis}
                stroke={line.color}
                strokeDasharray={line.strokeDasharray}
                strokeWidth={line.strokeWidth}
                legendType={line.legendType}
                label="Test"
                name={line.name}
                dot={<CustomizedDot key={line.name} />}
              />
            ))}
          {chartLines
            .filter(
              (l) =>
                l.reference &&
                ![
                  'User Defined LTL',
                  'User Defined UTL',
                  'User Defined LCL',
                  'User Defined UCL'
                ].includes(l.name)
            )
            .map((line) => (
              <ReferenceLine
                y={
                  line.overrideValue ??
                  (chartData?.length > 0
                    ? chartData[chartData.length - 1]?.[line.yAxis]
                    : undefined)
                }
                key={`ref-${line.yAxis}`}
                stroke="transparent"
                label={line.label}
              />
            ))}
          {chartLines
            .filter(
              (l) => l.reference && (l.name === 'User Defined LTL' || l.name === 'User Defined UTL')
            )
            .map((line) => renderToleranceLines(line))}
          {chartLines
            .filter(
              (l) => l.reference && l.name !== 'User Defined LCL' && l.name !== 'User Defined UCL'
            )
            .map((line) => (
              <ReferenceLine
                y={
                  line.overrideValue ??
                  (chartData?.length > 0
                    ? chartData[chartData.length - 1]?.[line.yAxis]
                    : undefined)
                }
                key={line.name}
                stroke="transparent"
                label={line.label}
              />
            ))}
          {chartLines
            .filter(
              (l) => l.reference && (l.name === 'User Defined LCL' || l.name === 'User Defined UCL')
            )
            .map((line) => renderControlLines(line))}
        </>
      );
    }

    return (
      <>
        {chartLines
          .filter((l) => l.reference)
          .map((line) => (
            <Line
              type="linear"
              key={`ref-${line.yAxis}`}
              isAnimationActive={false}
              dataKey={line.yAxis}
              stroke="transparent"
              label=""
              name={line.name}
              dot={<CustomizedDot key={line.name} />}
            />
          ))}
        {chartLines
          .filter((l) => l.reference)
          .map((line) => (
            <ReferenceLine
              y={
                line.overrideValue ??
                (chartData?.length > 0 ? chartData[chartData.length - 1]?.[line.yAxis] : undefined)
              }
              key={`ref-${line.yAxis}`}
              stroke="transparent"
              label=""
            />
          ))}
      </>
    );
  };

  const checkWarning = (payload) => {
    return wFlagIChartColumns.find((f) => !!payload[f]) !== undefined;
  };

  const checkOOC = (payload) => {
    return oocFlagIChartColumns.find((f) => !!payload[f]) !== undefined;
  };

  // EverFlags are evaluated for EVERY point (not only omitted/silenced ones) so the
  // overlay colours the whole series when it is on. createCustomizedDot gates the
  // actual colouring on everFlagsChecked and applies OOC-over-warning precedence.
  // Stored everFlags are first re-evaluated against the key's live config, so
  // flags frozen on locked rows from a stale config snapshot don't colour points.
  const everFlagsConfig = liveEverFlagsConfig(currentControlStrategyConfigurations);

  const checkEverFlagsWarning = (payload) => everFlagsHasWarning(payload, everFlagsConfig);

  const checkEverFlagsOOC = (payload) => everFlagsHasOOC(payload, everFlagsConfig);

  const CustomizedDot = ({ cx, cy, payload, dataKey }) => {
    let dotToReturn = [];
    if (dataKey === 'resultOmits') {
      const isWarning = checkWarning(payload);
      const isOOC = checkOOC(payload);

      const isEverFlagsWarning = checkEverFlagsWarning(payload);
      const isEverFlagsOOC = checkEverFlagsOOC(payload);

      dotToReturn = createCustomizedDot(
        cx,
        cy,
        payload,
        setupDataChecked,
        isWarning,
        isOOC,
        warningFlagsChecked,
        outOfControlChecked,
        everFlagsChecked,
        isEverFlagsWarning,
        isEverFlagsOOC
      );
    }

    return dotToReturn;
  };

  const renderDataLines = (rcc) => {
    if (rcc) {
      return (
        <>
          {chartLines
            .filter((l) => !l.reference)
            .map((line) => (
              <Line
                type="linear"
                key={`data-${line.yAxis}`}
                isAnimationActive={false}
                dataKey={line.yAxis}
                stroke={line.color}
                name={line.name}
                dot={<CustomizedDot key={line.yAxis} />}
              />
            ))}
        </>
      );
    }
    return (
      <>
        <Line
          type="linear"
          key={'resultOmits' || 'Data'}
          isAnimationActive={false}
          dataKey="resultOmits"
          stroke="#32CD32"
          name="Data"
          dot={<CustomizedDot key={'resultOmits'} />}
        />
      </>
    );
  };

  // Opt-in (showDateTimeInTooltip): date/time of the hovered point, formatted like the
  // results table. '' when off or when the row has no date, so nothing is rendered.
  const getPointDateTime = (row) =>
    showDateTimeInTooltip ? formatChartPointDateTime(row, tz, dateTimeFormat) : '';

  const EverFlagsTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const pointDateTime = getPointDateTime(data);
      // Show the everFlags list whenever the row carries everFlags — matching the
      // dot colouring, which now colours EVERY point from everFlags (not only
      // omitted/silenced ones). Otherwise a point coloured red/yellow would show
      // "None" in its tooltip (Arteco/Thijs). Same live-config filter as the dots.
      const flags = getEverFlagsString(data, everFlagsConfig);
      const shouldShowEverFlags = flags.length > 0;

      // Render the full series list (data point + control limits/stats) exactly
      // like the default Recharts tooltip, so toggling EverFlags on no longer
      // drops the control-limit rows from the popup (fixes #405).
      const seriesRows = resolveEverFlagsTooltipRows(payload, data).map((row, index) => (
        <p key={`series-${index}`} style={{ color: row.color }}>
          {row.name} : {renderSigDigits(row.value)}
        </p>
      ));

      if (shouldShowEverFlags) {
        return (
          <div className="custom-tooltip">
            <p>{label}</p>
            {pointDateTime && <p>{pointDateTime}</p>}
            {seriesRows}
            <div>
              {flags.map((flag, index) => {
                let color = 'black';
                if (flag.includes('Warnings:') || flag.includes('W:')) {
                  color = 'gold';
                } else if (flag.includes('OOC:')) {
                  color = 'red';
                }
                return (
                  <p
                    key={`flag-${index}`}
                    style={{
                      color: color
                    }}
                  >
                    {flag.trim()}
                  </p>
                );
              })}
            </div>
          </div>
        );
      }

      return (
        <div className="custom-tooltip">
          <p>{label}</p>
          {pointDateTime && <p>{pointDateTime}</p>}
          {seriesRows}
          <p>None</p>
        </div>
      );
    }

    return null;
  };

  const uniqueParameters = Array.from(
    new Set(
      data
        .map((d) => {
          if (d.fullKey) {
            const parts = d.fullKey.split('|');
            return parts[parts.length - 1];
          }
          return d.parameter;
        })
        .filter(Boolean)
    )
  );

  return (
    <>
      <h5>
        I-Chart
        {uniqueParameters?.length
          ? `: ${uniqueParameters.join(', ')}${unit?.trim() ? `, ${unit}` : ''}`
          : unit?.trim()
            ? `: ${unit}`
            : ''}
      </h5>

      <div className="checkbox-buttons-container">
        <div className="checkbox-buttons">
          Setup data = 1 <Form.Check value={setupDataChecked} onChange={handleSetupDataCheck} />
        </div>
        <div className="checkbox-buttons">
          Warning flags{' '}
          <Form.Check checked={warningFlagsChecked} onChange={handleWarningFlagCheck} />
        </div>
        <div className="checkbox-buttons">
          Out of Control{' '}
          <Form.Check checked={outOfControlChecked} onChange={handleOutOfControlCheck} />
        </div>
        <div className="checkbox-buttons">
          Reference Lines <Form.Check checked={runChartsChecked} onChange={handleRunChartsCheck} />
        </div>
        {canShowArv && (
          <div className="checkbox-buttons">
            Show ARV{' '}
            <Form.Check
              aria-label="Show ARV"
              checked={arvChecked}
              disabled={!runChartsChecked}
              title={runChartsChecked ? undefined : 'Turn on Reference Lines to show the ARV'}
              onChange={handleShowArvCheck}
            />
          </div>
        )}
        {isEverFlags && (
          <div className="checkbox-buttons">
            EverFlags <Form.Check checked={everFlagsChecked} onChange={handleEverFlagsCheck} />
          </div>
        )}
      </div>
      <ResponsiveContainer height={400}>
        <LineChart
          data={chartData}
          syncId={syncId}
          margin={{
            top: 20,
            right: 20,
            bottom: 15,
            left: 20
          }}
        >
          <Legend verticalAlign="top" margin height={80} />
          {isSpcDashboard && (
            <Customized
              component={({ width }) => (
                <foreignObject x={0} y={40} width={width} height={100}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      height: '50%'
                    }}
                  >
                    <h3 className="info-spc-container">
                      <span className="info-spc-label">Process Mean: </span>
                      {isFinite(parseFloat(selectedSummaryInfo?.mean_process_or))
                        ? parseFloat(selectedSummaryInfo.mean_process_or).toFixed(2)
                        : '--'}

                      <span className="info-spc-label">Process Range Mean: </span>
                      {isFinite(parseFloat(selectedSummaryInfo?.r_bar_sd))
                        ? parseFloat(selectedSummaryInfo.r_bar_sd).toFixed(2)
                        : '--'}
                      {config?.adjustBlueStatisticsLineSpcCharts ? (
                        <>
                          <span className="info-spc-label">Process std dev, theta-hat: </span>
                          {isFinite(parseFloat(selectedSummaryInfo?.stdev_process_or))
                            ? parseFloat(selectedSummaryInfo.stdev_process_or).toFixed(2)
                            : '--'}
                          <span className="info-spc-label">UCL x-bar: </span>
                          {isFinite(parseFloat(selectedSummaryInfo?.ucl_sd))
                            ? parseFloat(selectedSummaryInfo.ucl_sd).toFixed(2)
                            : '--'}
                          <span className="info-spc-label">LCL x-bar: </span>
                          {isFinite(parseFloat(selectedSummaryInfo?.lcl_sd))
                            ? parseFloat(selectedSummaryInfo.lcl_sd).toFixed(2)
                            : '--'}
                        </>
                      ) : (
                        <>
                          <span className="info-spc-label">Process Capability (Cp): </span>
                          {isFinite(parseFloat(selectedSummaryInfo?.Cp))
                            ? parseFloat(selectedSummaryInfo.Cp).toFixed(2)
                            : '--'}

                          <span className="info-spc-label">Process Capability (Cpk): </span>
                          {isFinite(parseFloat(selectedSummaryInfo?.Cpk))
                            ? parseFloat(selectedSummaryInfo.Cpk).toFixed(2)
                            : '--'}

                          <span className="info-spc-label">Percent Yield: </span>
                          {isFinite(parseFloat(selectedSummaryInfo?.percent_yield))
                            ? parseFloat(selectedSummaryInfo.percent_yield).toFixed(2)
                            : '--'}
                        </>
                      )}
                    </h3>
                  </div>
                </foreignObject>
              )}
            />
          )}
          {renderDataLines(runChartsChecked)}
          {renderReferenceLines(runChartsChecked)}
          <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
          <XAxis dataKey={xAxis} tickFormatter={formatXAxis} />
          <YAxis type="number" tickFormatter={formatYAxis} domain={yDomain} />
          <Brush dataKey={xAxis} />
          {everFlagsChecked ? (
            <Tooltip content={<EverFlagsTooltip />} />
          ) : uniqueParameters.length > 1 ? (
            <Tooltip
              formatter={(value) => `${renderSigDigits(value)}`}
              labelFormatter={(label) => {
                let parameter = '';
                if (data && data[label] && data[label].fullKey) {
                  const parts = data[label].fullKey.split('|');
                  parameter = parts[parts.length - 1];
                } else if (data && data[label] && data[label].parameter) {
                  parameter = data[label].parameter;
                }
                const text = `${label}${parameter ? ` - ${parameter}` : ''}`;
                const pointDateTime = getPointDateTime(data && data[label]);
                return withPopupDateTime(text, pointDateTime);
              }}
            />
          ) : showDateTimeInTooltip ? (
            <Tooltip
              formatter={(value) => `${renderSigDigits(value)}`}
              labelFormatter={(label) => {
                const pointDateTime = getPointDateTime(data && data[label]);
                return withPopupDateTime(label, pointDateTime);
              }}
            />
          ) : (
            <Tooltip formatter={(value) => `${renderSigDigits(value)}`} />
          )}
        </LineChart>
      </ResponsiveContainer>
    </>
  );
}

SQCLineChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  lines: PropTypes.arrayOf(PropTypes.object),
  xAxis: PropTypes.string.isRequired,
  syncId: PropTypes.string.isRequired,
  yMin: PropTypes.number.isRequired,
  yMax: PropTypes.number.isRequired,
  controlStrategyConfigurations: PropTypes.object,
  arv: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showArv: PropTypes.bool,
  onShowArvChange: PropTypes.func,
  showDateTimeInTooltip: PropTypes.bool,
  tz: PropTypes.string,
  dateTimeFormat: PropTypes.string
};

export default SQCLineChart;
